import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";

import { Button, Checkbox, Form, Input, Modal, Select, Switch, Table } from "antd";
import {
  CardTitle,
  DetailList,
  ListToolbar,
  PageHeader,
  RowActions,
  SectionCard,
  StatusTag,
  TabBar,
  fmtDateTime,
  tablePagination,
  useFlatPage,
} from "../components/ui";
import { IconPlus } from "../components/ui/icons";
import notify from "../utils/notify";

const { Option } = Select;
const { confirm } = Modal;

/* ---------- small helper: de-duplicate by id ---------------- */
const dedupById = (arr, idKey = "id") =>
  Array.from(new Map(arr.map((x) => [x[idKey], x])).values());

/* ============================================================ */
const ManageStaffPage = () => {
  useFlatPage();
  /* ------------ state / hooks ------------------------------ */
  const { authData } = useAuth();

  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);           // users in the selected role
  const [allUsers, setAllUsers] = useState([]);     // all users in hotel
  const [roleUserCounts, setRoleUserCounts] = useState({}); // cached counts per role

  const [loading, setLoading] = useState({
    roles: false,
    users: false,
    permissions: false,
    rolePermissions: false,
    userPermissions: false
  });

  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const [addRoleModalVisible, setAddRoleModalVisible] = useState(false);
  const [addUserModalVisible, setAddUserModalVisible] = useState(false);
  const [editRoleModalVisible, setEditRoleModalVisible] = useState(false);
  const [permissionModalVisible, setPermissionModalVisible] = useState(false);

  const [form] = Form.useForm();
  const [userForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [permissionForm] = Form.useForm();

  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("users");

  const [permissions, setPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);

  /* ------------ data fetching ---------------------------- */
  const fetchRoles = useCallback(async () => {
    if (!authData?.hotelId) return;
    try {
      setLoading(prev => ({ ...prev, roles: true }));
      const { data } = await axiosInstance.get(`/roles/byhotel/${authData.hotelId}`);
      setRoles(
        (data || []).map(r => ({
          id: r.id,
          name: r.name,
          description: r.description,
          hotelId: r.hotelId,
          createdAt: r.createdAt ?? null
        }))
      );
    } catch (err) {
      console.error(err);
      notify.error({
        title: "Error",
        description: "Failed to fetch roles. Please try again later."
      });
    } finally {
      setLoading(prev => ({ ...prev, roles: false }));
    }
  }, [authData?.hotelId]);

  const fetchUsers = useCallback(async () => {
    if (!authData?.hotelId) return;
    try {
      setLoading(prev => ({ ...prev, users: true }));
      const { data } = await axiosInstance.get(`/User?hotelId=${authData.hotelId}`);
      setAllUsers(data || []);
    } catch (err) {
      console.error(err);
      notify.error({
        title: "Error",
        description: "Failed to fetch users. Please try again later."
      });
    } finally {
      setLoading(prev => ({ ...prev, users: false }));
    }
  }, [authData?.hotelId]);

  const fetchPermissions = useCallback(async () => {
    if (!authData?.hotelId) return;
    try {
      setLoading(prev => ({ ...prev, permissions: true }));
      const { data } = await axiosInstance.get(`/RolePermission/Permissions?hotelId=${authData.hotelId}`);
      setPermissions(data || []);
    } catch (err) {
      console.error(err);
      notify.error({
        title: "Error",
        description: "Failed to fetch permissions. Please try again later."
      });
    } finally {
      setLoading(prev => ({ ...prev, permissions: false }));
    }
  }, [authData?.hotelId]);

  const fetchRolePermissions = useCallback(async (roleId) => {
    if (!roleId) return;
    try {
      setLoading(prev => ({ ...prev, rolePermissions: true }));
      const { data } = await axiosInstance.get(`/RolePermission/Roles/${roleId}/Permissions`);
      setRolePermissions((data || []).map(p => p.permissionId));
    } catch (err) {
      notify.error({
        title: "Error",
        description: "Failed to load role permissions"
      });
      setRolePermissions([]);
    } finally {
      setLoading(prev => ({ ...prev, rolePermissions: false }));
    }
  }, []);

  const fetchUserPermissions = useCallback(async (userId) => {
    if (!userId) return;
    try {
      setLoading(prev => ({ ...prev, userPermissions: true }));
      const { data } = await axiosInstance.get(`/RolePermission/Users/${userId}/Permissions`);
      setUserPermissions((data || []).map(p => p.permissionId));
    } catch (err) {
      notify.error({
        title: "Error",
        description: "Failed to load user permissions"
      });
      setUserPermissions([]);
    } finally {
      setLoading(prev => ({ ...prev, userPermissions: false }));
    }
  }, []);

  // NEW: authoritative membership fetch
  const fetchUsersForRole = useCallback(async (roleId) => {
  if (!roleId) return [];
  try {
    const { data } = await axiosInstance.get(`/roles/${roleId}/users`);
    return Array.isArray(data) ? data : [];
  } catch (err) {
    if (err?.response?.status === 405 || err?.response?.status === 404) {
      const { data } = await axiosInstance.get(`/roles/WithUsers`, {
        params: { hotelId: authData.hotelId }
      });
      const role = (data || []).find(r => r.roleId === roleId);
      return role?.users?.map(u => ({
        id: u.userId,
        firstName: u.firstName,
        lastName: u.lastName,
        email: u.email,
        isActive: u.isActive
      })) || [];
    }
    throw err;
  }
}, [authData?.hotelId]);


  // Initial data load
  useEffect(() => {
    (async () => {
      await Promise.all([fetchRoles(), fetchUsers(), fetchPermissions()]);
    })();
  }, [fetchRoles, fetchUsers, fetchPermissions]);

  /* ------------ helpers ---------------------------------- */
  const handleRoleSelect = useCallback(async (role) => {
    if (!role) return;
    setSelectedRole(role);
    setSelectedUser(null);

    // Authoritative list of users who are in this role
    const rUsers = await fetchUsersForRole(role.id);
    setUsers(rUsers);
    setRoleUserCounts(prev => ({ ...prev, [role.id]: rUsers.length }));

    // Load permissions for this role
    await fetchRolePermissions(role.id);
  }, [fetchRolePermissions, fetchUsersForRole]);

  const handleUserSelect = useCallback(async (user) => {
    if (!user) return;
    setSelectedUser(user);
    await fetchUserPermissions(user.id);
  }, [fetchUserPermissions]);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchRoles(), fetchUsers(), fetchPermissions()]);
    if (selectedRole) {
      const rUsers = await fetchUsersForRole(selectedRole.id);
      setUsers(rUsers);
      setRoleUserCounts(prev => ({ ...prev, [selectedRole.id]: rUsers.length }));
      await fetchRolePermissions(selectedRole.id);
    }
    if (selectedUser) {
      await fetchUserPermissions(selectedUser.id);
    }
  }, [
    fetchRoles,
    fetchUsers,
    fetchPermissions,
    fetchRolePermissions,
    fetchUserPermissions,
    fetchUsersForRole,
    selectedRole,
    selectedUser
  ]);

  /* ------------ CRUD: Roles ------------------------------ */
  const handleAddRole = async (vals) => {
    try {
      const { data: newRole } = await axiosInstance.post("/roles", {
        name: vals.name,
        description: vals.description,
        hotelId: authData.hotelId,
      });

      notify.success({ message: "Role created successfully" });
      setAddRoleModalVisible(false);
      form.resetFields();
      await fetchRoles();
      handleRoleSelect({
        id: newRole.id,
        name: newRole.name,
        description: newRole.description,
        hotelId: newRole.hotelId,
        createdAt: newRole.createdAt ?? null,
      });
    } catch (err) {
      notify.error({
        title: "Error",
        description: err.response?.data?.message || "Failed to create role",
      });
    }
  };

  const handleUpdateRole = async (vals) => {
    if (!selectedRole) return;
    try {
      await axiosInstance.put(`/roles/${selectedRole.id}`, {
        name: vals.name,
        description: vals.description,
      });

      notify.success({ message: "Role updated successfully" });
      setEditRoleModalVisible(false);
      editForm.resetFields();
      await fetchRoles();
    } catch (err) {
      notify.error({
        title: "Error",
        description: err.response?.data?.message || "Failed to update role",
      });
    }
  };

  const handleDeleteRole = (roleId) => {
    confirm({
      title: "Delete Role",
      content:
        "Are you sure you want to delete this role? Users assigned to this role will be unassigned.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await axiosInstance.delete(`/roles/${roleId}`);
          notify.success({ message: "Role deleted successfully" });

          if (selectedRole?.id === roleId) {
            setSelectedRole(null);
            setUsers([]);
            setRolePermissions([]);
          }
          await fetchRoles();
        } catch (err) {
          notify.error({
            title: "Error",
            description:
              err.response?.data?.message || "Failed to delete role",
          });
        }
      },
    });
  };

  /* ------------ CRUD: Users within a role ---------------- */
  const handleAddUserToRole = async (vals) => {
    if (!selectedRole || !vals.userId) return;
    try {
      await axiosInstance.post(`/roles/${selectedRole.id}/users`, {
        userId: vals.userId,
      });
      notify.success({ message: "User added to role successfully" });
      setAddUserModalVisible(false);
      userForm.resetFields();

      await fetchUsers(); // refresh global list (optional)
      const rUsers = await fetchUsersForRole(selectedRole.id); // refresh membership
      setUsers(rUsers);
      setRoleUserCounts(prev => ({ ...prev, [selectedRole.id]: rUsers.length }));
    } catch (err) {
      notify.error({
        title: "Error",
        description: err.response?.data?.message || "Failed to add user",
      });
    }
  };

  const handleRemoveUserFromRole = (userId) => {
    confirm({
      title: "Remove User",
      content: "Remove this user from the role?",
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await axiosInstance.delete(`/roles/RemoveUser`, {
            params: { roleId: selectedRole.id, userId },
          });
          notify.success({ message: "User removed successfully" });

          await fetchUsers(); // refresh global list (optional)
          const rUsers = await fetchUsersForRole(selectedRole.id);
          setUsers(rUsers);
          setRoleUserCounts(prev => ({ ...prev, [selectedRole.id]: rUsers.length }));
        } catch (err) {
          notify.error({
            title: "Error",
            description:
              err.response?.data?.message || "Failed to remove user",
          });
        }
      },
    });
  };

  /* ------------ User Permissions ------------------------- */
  const toggleUserPermission = async (permissionId, checked) => {
    if (!selectedUser) {
      return notify.warning({ message: 'Select a user first' });
    }
    try {
      setLoading(prev => ({ ...prev, userPermissions: true }));
      if (checked) {
        await axiosInstance.post(
          `/RolePermission/Users/${selectedUser.id}/Permissions`,
          { permissionIds: [permissionId] }
        );
        setUserPermissions(prev => [...prev, permissionId]);
      } else {
        await axiosInstance.delete(
          `/RolePermission/Users/${selectedUser.id}/Permissions/${permissionId}`
        );
        setUserPermissions(prev => prev.filter(id => id !== permissionId));
      }
    } catch (err) {
      notify.error({
        title: "Error",
        description: err.response?.data?.message || "Failed to update permission",
      });
    } finally {
      setLoading(prev => ({ ...prev, userPermissions: false }));
    }
  };

  const bulkAssignUserPermissions = async (ids) => {
    if (!selectedUser) return;
    try {
      setLoading(prev => ({ ...prev, userPermissions: true }));
      await axiosInstance.post(
        `/RolePermission/Users/${selectedUser.id}/Permissions`,
        { permissionIds: ids }
      );
      setUserPermissions(ids);
      notify.success({ message: "Permissions updated successfully" });
    } catch (err) {
      notify.error({
        title: "Error",
        description: err.response?.data?.message || "Failed to update permissions",
      });
    } finally {
      setLoading(prev => ({ ...prev, userPermissions: false }));
    }
  };

  /* ------------ User Status ------------------------------ */
  const handleToggleUserStatus = async (userId, isActive) => {
    try {
      await axiosInstance.put(`/User/${userId}/activate`, null, {
        params: { isActive: !isActive },
      });
      notify.success({
        title: `User ${isActive ? "deactivated" : "activated"} successfully`,
      });
      await fetchUsers();
    } catch (err) {
      notify.error({
        title: "Error",
        description:
          err.response?.data?.message ||
          `Failed to ${isActive ? "deactivate" : "activate"} user`,
      });
    }
  };

  /* ------------ Role Permissions ------------------------- */
  const handleUpdatePermissions = async (vals) => {
    if (!selectedRole) return;
    try {
      setLoading(prev => ({ ...prev, rolePermissions: true }));
      const currentUser = authData?.userName || "System";
      await axiosInstance.post(
        `/RolePermission/Roles/${selectedRole.id}/Permissions`,
        {
          permissionIds: vals.permissions,
          assignedBy: currentUser
        }
      );
      setRolePermissions(vals.permissions);
      notify.success({ message: "Permissions updated successfully" });
      setPermissionModalVisible(false);
      permissionForm.resetFields();
    } catch (err) {
      notify.error({
        title: "Error",
        description: err.response?.data?.message || "Failed to update permissions",
      });
    } finally {
      setLoading(prev => ({ ...prev, rolePermissions: false }));
    }
  };

  const toggleRolePermission = async (permissionId, checked) => {
    if (!selectedRole) {
      return notify.warning({ message: 'Select a role first' });
    }
    try {
      setLoading(prev => ({ ...prev, rolePermissions: true }));
      const currentUser = authData?.userName || "System";
      if (checked) {
        await axiosInstance.post(
          `/RolePermission/Roles/${selectedRole.id}/Permissions`,
          {
            permissionIds: [permissionId],
            assignedBy: currentUser
          }
        );
        setRolePermissions(prev => [...prev, permissionId]);
      } else {
        await axiosInstance.delete(
          `/RolePermission/Roles/${selectedRole.id}/Permissions/${permissionId}`
        );
        setRolePermissions(prev => prev.filter(id => id !== permissionId));
      }
    } catch (err) {
      notify.error({
        title: "Error",
        description: err.response?.data?.message || "Failed to update permission",
      });
    } finally {
      setLoading(prev => ({ ...prev, rolePermissions: false }));
    }
  };

  const bulkAssignRolePermissions = async (ids) => {
    if (!selectedRole) return;
    try {
      setLoading(prev => ({ ...prev, rolePermissions: true }));
      const currentUser = authData?.userName || "System";
      await axiosInstance.post(
        `/RolePermission/Roles/${selectedRole.id}/Permissions`,
        {
          permissionIds: ids,
          assignedBy: currentUser
        }
      );
      setRolePermissions(ids);
      notify.success({ message: "Permissions updated successfully" });
    } catch (err) {
      notify.error({
        title: "Error",
        description: err.response?.data?.message || "Failed to update permissions",
      });
    } finally {
      setLoading(prev => ({ ...prev, rolePermissions: false }));
    }
  };

  /* ------------ derived data ----------------------------- */
  const filteredRoles = roles.filter((r) =>
    r.name?.toLowerCase().includes(searchText.toLowerCase())
  );

  // Build "available users" by set difference: allUsers minus users currently assigned to the selected role
  const assignedIds = new Set(users.map(u => String(u.id)));
  const availableUsers = dedupById(
    allUsers.filter(u => u.id && !assignedIds.has(String(u.id))),
    "id"
  );

  /* ------------ helpers for the clear-all buttons --------- */
  const clearRolePermissions = async () => {
    try {
      setLoading(prev => ({ ...prev, rolePermissions: true }));
      await axiosInstance.delete(`/RolePermission/Roles/${selectedRole.id}/Permissions`);
      setRolePermissions([]);
      notify.success({ title: "Permissions cleared" });
    } catch (err) {
      notify.error({ title: "Error", description: err.response?.data?.message || "Failed to clear permissions" });
    } finally {
      setLoading(prev => ({ ...prev, rolePermissions: false }));
    }
  };

  const clearUserPermissions = async () => {
    try {
      setLoading(prev => ({ ...prev, userPermissions: true }));
      await axiosInstance.delete(`/RolePermission/Users/${selectedUser.id}/Permissions`);
      setUserPermissions([]);
      notify.success({ title: "Permissions cleared" });
    } catch (err) {
      notify.error({ title: "Error", description: err.response?.data?.message || "Failed to clear permissions" });
    } finally {
      setLoading(prev => ({ ...prev, userPermissions: false }));
    }
  };

  /* ------------ columns ---------------------------------- */
  const roleColumns = [
    {
      title: "Role",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div className="hms-cell-stack">
          <span className="hms-cell-main">{text || "Unnamed role"}</span>
          <span className="hms-cell-sub">{record.description || "No description"}</span>
        </div>
      ),
    },
    {
      title: "Users",
      key: "userCount",
      width: 80,
      align: "right",
      render: (_, record) => {
        const count =
          typeof roleUserCounts[record.id] === "number"
            ? roleUserCounts[record.id]
            : selectedRole?.id === record.id
            ? users.length
            : 0;
        return <span className="hms-count">{count}</span>;
      },
    },
    {
      title: "",
      key: "actions",
      width: 130,
      align: "right",
      render: (_, rec) => (
        <span onClick={(e) => e.stopPropagation()}>
          <RowActions
            actions={[
              {
                key: "edit",
                label: "Edit role",
                icon: "edit",
                onClick: () => {
                  setSelectedRole(rec);
                  editForm.setFieldsValue({ name: rec.name, description: rec.description });
                  setEditRoleModalVisible(true);
                },
              },
              {
                key: "permissions",
                label: "Manage permissions",
                icon: "permissions",
                onClick: async () => {
                  setSelectedRole(rec);
                  await handleRoleSelect(rec); // make sure we have the latest membership and permissions
                  setPermissionModalVisible(true);
                },
              },
              {
                key: "delete",
                label: "Delete role",
                icon: "delete",
                danger: true,
                onClick: () => handleDeleteRole(rec.id),
              },
            ]}
          />
        </span>
      ),
    },
  ];

  const permissionColumnsFor = (assigned, onToggle, disabled) => [
    {
      title: "Permission",
      dataIndex: "name",
      render: (text, record) => (
        <div className="hms-cell-stack">
          <span className="hms-cell-main">{text}</span>
          <span className="hms-cell-sub">{record.category || "General"}</span>
        </div>
      ),
    },
    {
      title: "Status",
      width: 130,
      render: (_, record) => {
        const on = assigned.includes(record.permissionId);
        return <StatusTag status={on ? "available" : ""}>{on ? "Assigned" : "Not assigned"}</StatusTag>;
      },
    },
    {
      title: "",
      width: 70,
      align: "right",
      render: (_, record) => (
        <Checkbox
          checked={assigned.includes(record.permissionId)}
          onChange={(e) => onToggle(record.permissionId, e.target.checked)}
          disabled={disabled}
        />
      ),
    },
  ];

  const permissionColumns = permissionColumnsFor(
    rolePermissions,
    toggleRolePermission,
    !selectedRole || loading.rolePermissions
  );
  const userPermissionColumns = permissionColumnsFor(
    userPermissions,
    toggleUserPermission,
    !selectedUser || loading.userPermissions
  );

  const initials = (u) => `${(u.firstName || "U")[0]}${(u.lastName || "")[0] || ""}`.toUpperCase();

  const tabs = [
    { key: "users", label: "Users", count: users.length },
    { key: "permissions", label: "Permissions", count: rolePermissions.length },
    { key: "details", label: "Details" },
  ];

  const roleDetail = () => {
    if (activeTab === "users") {
      return users.length > 0 ? (
        <ul className="hms-checklist" style={{ padding: "0 20px" }}>
          {users.map((u) => (
            <li key={u.id}>
              <span className="hms-person">
                <span className="hms-avatar">{initials(u)}</span>
                <span className="hms-cell-stack">
                  <span className="hms-cell-main">{`${u.firstName || ""} ${u.lastName || ""}`.trim() || "Unnamed user"}</span>
                  <span className="hms-cell-sub">{u.email || "No email"}</span>
                </span>
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                <StatusTag status={u.isActive ? "available" : "cancelled"}>{u.isActive ? "Active" : "Inactive"}</StatusTag>
                <Switch
                  size="small"
                  checked={u.isActive}
                  onChange={() => handleToggleUserStatus(u.id, u.isActive)}
                  aria-label={u.isActive ? "Deactivate user" : "Activate user"}
                />
                <RowActions
                  actions={[
                    { key: "perm", label: "Manage permissions", icon: "permissions", onClick: () => handleUserSelect(u) },
                    {
                      key: "remove",
                      label: "Remove from role",
                      icon: "unassign",
                      danger: true,
                      onClick: () => handleRemoveUserFromRole(u.id),
                    },
                  ]}
                />
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="hms-empty">
          No users in this role.{" "}
          <Button type="link" onClick={() => setAddUserModalVisible(true)} disabled={availableUsers.length === 0}>
            Assign users
          </Button>
        </div>
      );
    }

    if (activeTab === "permissions") {
      return (
        <>
          <div className="hms-form-actions" style={{ justifyContent: "flex-start", padding: "12px 20px 0" }}>
            <Button
              type="primary"
              onClick={() => bulkAssignRolePermissions(permissions.map((p) => p.permissionId))}
              loading={loading.rolePermissions}
            >
              Assign all
            </Button>
            <Button danger onClick={clearRolePermissions} loading={loading.rolePermissions}>
              Clear all
            </Button>
          </div>
          <Table
            className="hms-table"
            rowKey="permissionId"
            columns={permissionColumns}
            dataSource={permissions}
            loading={loading.permissions || loading.rolePermissions}
            pagination={tablePagination("permission")}
            style={{ marginTop: 12 }}
          />
        </>
      );
    }

    return (
      <div style={{ padding: "8px 20px 16px" }}>
        <DetailList
          items={[
            { label: "Role name", value: selectedRole?.name },
            { label: "Description", value: selectedRole?.description || "No description" },
            { label: "Created", value: selectedRole?.createdAt ? fmtDateTime(selectedRole.createdAt) : null },
            { label: "Users", value: users.length },
            { label: "Permissions", value: rolePermissions.length },
          ]}
        />
      </div>
    );
  };

  /* ------------ render ----------------------------------- */
  return (
    <div className="hms-page ms-page">
      <PageHeader
        actions={
          <Button type="primary" icon={<IconPlus />} onClick={() => setAddRoleModalVisible(true)}>
            Add role
          </Button>
        }
      />

      <div className="hms-split">
        {/* LEFT: roles */}
        <SectionCard
          flush
          title={<CardTitle count={roles.length}>Roles</CardTitle>}
          extra={
            <ListToolbar
              search={{ value: searchText, onChange: setSearchText, placeholder: "Search roles" }}
              onRefresh={refreshAll}
              loading={loading.roles || loading.users}
            />
          }
        >
          <Table
            className="hms-table"
            rowKey="id"
            columns={roleColumns}
            dataSource={filteredRoles}
            loading={loading.roles}
            pagination={tablePagination("role", { hideOnSinglePage: true })}
            onRow={(rec) => ({ onClick: () => handleRoleSelect(rec), style: { cursor: "pointer" } })}
            rowClassName={(rec) => (selectedRole?.id === rec.id ? "selected-row" : "")}
            locale={{ emptyText: <div className="hms-empty">No roles found. Create your first role.</div> }}
          />
        </SectionCard>

        {/* RIGHT: details */}
        {selectedUser ? (
          <SectionCard
            flush
            title={`${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim() || "User"}
            extra={
              <div className="hms-form-actions" style={{ marginTop: 0 }}>
                <Button onClick={() => setSelectedUser(null)}>Back to role</Button>
                <Button
                  type="primary"
                  onClick={() => bulkAssignUserPermissions(permissions.map((p) => p.permissionId))}
                  loading={loading.userPermissions}
                >
                  Assign all
                </Button>
                <Button danger onClick={clearUserPermissions} loading={loading.userPermissions}>
                  Clear all
                </Button>
              </div>
            }
          >
            <Table
              className="hms-table"
              rowKey="permissionId"
              columns={userPermissionColumns}
              dataSource={permissions}
              loading={loading.permissions || loading.userPermissions}
              pagination={tablePagination("permission")}
            />
          </SectionCard>
        ) : selectedRole ? (
          <SectionCard
            flush
            title={<CardTitle count={users.length}>{selectedRole.name || "Role"}</CardTitle>}
            extra={
              <Button type="primary" icon={<IconPlus />} onClick={() => setAddUserModalVisible(true)} disabled={availableUsers.length === 0}>
                Add user
              </Button>
            }
          >
            <TabBar tabs={tabs} value={activeTab} onChange={setActiveTab} />
            {roleDetail()}
          </SectionCard>
        ) : (
          <SectionCard>
            <div className="hms-empty">Select a role to see its users and permissions</div>
          </SectionCard>
        )}
      </div>

      {/* ---------- Add role ---------- */}
      <Modal
        title="Add role"
        open={addRoleModalVisible}
        onCancel={() => {
          setAddRoleModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={480}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={handleAddRole}>
          <Form.Item name="name" label="Role name" rules={[{ required: true, message: "Please enter a role name" }]}>
            <Input placeholder="e.g. Housekeeping Manager" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="What this role is for" />
          </Form.Item>
          <div className="hms-form-actions">
            <Button
              onClick={() => {
                setAddRoleModalVisible(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              Create role
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ---------- Edit role ---------- */}
      {selectedRole && (
        <Modal
          title={`Edit role: ${selectedRole.name}`}
          open={editRoleModalVisible}
          onCancel={() => {
            setEditRoleModalVisible(false);
            editForm.resetFields();
          }}
          footer={null}
          width={480}
          forceRender
        >
          <Form form={editForm} layout="vertical" onFinish={handleUpdateRole}>
            <Form.Item name="name" label="Role name" rules={[{ required: true, message: "Please enter a role name" }]}>
              <Input placeholder="e.g. Housekeeping Manager" />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <Input.TextArea rows={3} placeholder="What this role is for" />
            </Form.Item>
            <div className="hms-form-actions">
              <Button
                onClick={() => {
                  setEditRoleModalVisible(false);
                  editForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Save changes
              </Button>
            </div>
          </Form>
        </Modal>
      )}

      {/* ---------- Add user to role ---------- */}
      {selectedRole && (
        <Modal
          title={`Add user to ${selectedRole.name}`}
          open={addUserModalVisible}
          onCancel={() => {
            setAddUserModalVisible(false);
            userForm.resetFields();
          }}
          footer={null}
          width={480}
          destroyOnHidden
        >
          <Form form={userForm} layout="vertical" onFinish={handleAddUserToRole}>
            <Form.Item name="userId" label="User" rules={[{ required: true, message: "Please select a user" }]}>
              <Select
                placeholder="Select a user"
                showSearch
                optionFilterProp="children"
                filterOption={(input, opt) => String(opt?.children || "").toLowerCase().includes(input.toLowerCase())}
              >
                {availableUsers.map((u, idx) => (
                  <Select.Option key={`${u.id}-${idx}`} value={u.id}>
                    {`${u.firstName || ""} ${u.lastName || ""} (${u.email || "no email"})`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <div className="hms-form-actions">
              <Button
                onClick={() => {
                  setAddUserModalVisible(false);
                  userForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Add to role
              </Button>
            </div>
          </Form>
        </Modal>
      )}

      {/* ---------- Manage permissions ---------- */}
      {selectedRole && (
        <Modal
          title={`Permissions for ${selectedRole.name}`}
          open={permissionModalVisible}
          onCancel={() => {
            setPermissionModalVisible(false);
            permissionForm.resetFields();
          }}
          footer={null}
          width={720}
          destroyOnHidden
        >
          <Form
            form={permissionForm}
            layout="vertical"
            onFinish={handleUpdatePermissions}
            initialValues={{ permissions: rolePermissions }}
          >
            <Form.Item name="permissions" label="Permissions">
              <Select mode="multiple" placeholder="Select permissions" style={{ width: "100%" }}>
                {permissions.map((p) => (
                  <Select.Option key={p.permissionId} value={p.permissionId}>
                    {p.name} ({p.category || "No category"})
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <div className="hms-form-actions">
              <Button
                onClick={() => {
                  setPermissionModalVisible(false);
                  permissionForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Update permissions
              </Button>
            </div>
          </Form>
        </Modal>
      )}
    </div>
  );
};

export default ManageStaffPage;
